import { makeAutoObservable, runInAction } from "mobx";
import { ISignUp, IUser, IVerificationDocument } from "@/interfaces";
import RestAPIClient from "@/repo/restApi";
import gql from "@/repo/apolloClient";
import jwt_decode from "jwt-decode";
import { VERIFICATION_STATUS } from "@/@types/EnumType";
import { IMyPayment, IPaymentAttribute } from "@/interfaces/user";
import { disconnect } from "@wagmi/core";
import Cookies from "js-cookie";
interface State {
  myPayments: IMyPayment[];
  myprofile?: IUser | null;
  email?: string;
  verificationDocuments?: IVerificationDocument[];
}
export class UserStore {
  private controllerName = "users";
  private state: State = {
    myprofile: undefined,
    myPayments: [],
  };
  private unsubscriptionMyProfile: (() => void) | null = null;
  restApi: RestAPIClient<IUser>;

  constructor() {
    this.restApi = new RestAPIClient(this.controllerName);
    makeAutoObservable(this);
    const user = localStorage.getItem("user");

    if (user) {
      const userObj = JSON.parse(user);
      this.fetchProfile({ userID: userObj.UserID });
    } else {
      runInAction(() => {
        this.state.myprofile = null;
      });
    }
  }

  //#region getters
  public get isLogin() {
    if (this.state.myprofile !== undefined) {
      return !!this.state.myprofile;
    } else {
      return this.state.myprofile;
    }
  }
  public get isAccountVerified() {
    if (this.state.myprofile === undefined) {
      return undefined;
    } else if (
      this.state.myprofile?.account_verification_status ===
        VERIFICATION_STATUS.COMPLETED &&
      this.state.myprofile?.verified_level > 0
    ) {
      return true;
    } else {
      return false;
    }
  }
  public get isPhoneVerified() {
    return this.state.myprofile?.phone_number_verification_status;
  }
  public get profile() {
    return this.state.myprofile;
  }
  public get userId() {
    return this.state.myprofile?.user_id;
  }
  public get email() {
    return this.state.email;
  }
  public get wallet_address() {
    return this.state.myprofile?.wallet_address;
  }
  public get myPayments() {
    return this.state.myPayments;
  }
  public get username() {
    return this.state.myprofile?.username || null;
  }
  public get fullname() {
    if (this.state.myprofile) {
      return (
        this.state.myprofile.firstname + " " + this.state.myprofile.lastname
      );
    } else {
      return null;
    }
  }
  public accessToken() {
    const user = localStorage.getItem("user");
    if (user) {
      const userObj = JSON.parse(user);
      return userObj.access_token;
    }
  }
  public get verificationDocuments() {
    return this.state.verificationDocuments;
  }
  //#endregion
  //#region fetchers
  private waitForProfile = (): Promise<boolean | null> => {
    return new Promise((resolve) => {
      const checkProfile = () => {
        if (this.state.myprofile || this.state.myprofile === null) {
          const verified_level = this.state.myprofile?.verified_level;
          resolve(verified_level ? verified_level > 0 : null);
        } else {
          setTimeout(checkProfile, 100);
        }
      };
      checkProfile();
    });
  };
  public fetchProfile = async (request?: { userID?: string }) => {
    let UserID: string;
    if (request?.userID) {
      UserID = request.userID;
    } else if (this.state.myprofile?.user_id) {
      UserID = this.state.myprofile.user_id;
    } else {
      throw { message: "invalid user id" };
    }
    if (UserID) {
      const queryMyProfile = `
      subscription getMyProfile {
        users(where: {user_id: {_eq: "${UserID}"}}) {
          user_id
          username
          email
          created_at
          updated_at
          deleted_at
          firstname
          lastname
          phone_number
          email_verification_status
          phone_number_verification_status
          account_verification_status
          wallet_address
          verified_level
        }
      }`;
      const subscription = (await gql.Subscribe(queryMyProfile)).subscribe({
        next: (result) => {
          runInAction(() => {
            this.state.myprofile = result.data.users[0];
          });
        },
        error: () => {
          runInAction(() => {
            this.state.myprofile = null;
          });
        },
      });
      this.unsubscriptionMyProfile = () => {
        subscription.unsubscribe();
      };
    } else {
      runInAction(() => {
        this.state.myprofile = null;
      });
    }
    return true;
  };
  public fetchVerificationDocuments = async () => {
    const query = `
    query getVerificationDocuments {
      verification_documents{
        user_id
        admin_id
        purpose
        file_type
        file_url
        document_name
        status
        admin_comment
        created_at
        deleted_at
        updated_at
        filename
        file_key
        verification_document_id
        admin_commented_at
        documents_id
      }
    }
  `;
    const result = await gql.Query(query);
    if (result && result.data.verification_documents) {
      runInAction(() => {
        this.state.verificationDocuments = result.data.verification_documents;
      });
    } else {
      throw new Error("Failed to fetch verification documents");
    }
  };
  //#endregion
  //#region actions

  public updateProfile = async (request: {
    email?: string;
    firstname?: string;
    lastname?: string;
    phone_number?: string;
  }) => {
    if (!this.state.myprofile?.user_id) {
      throw new Error("User profile not found");
    }
    const queryUpdateProfile = `
      mutation updateProfile {
        update_users(
          where: {user_id: {_eq: "${this.state.myprofile.user_id}"}},
          _set: {
           
             ${request.email ? `email: "${request.email}",` : ""}
             ${request.firstname ? `firstname: "${request.firstname}",` : ""}
             ${request.lastname ? `lastname: "${request.lastname}",` : ""}
             ${
               request.phone_number
                 ? `phone_number: "${request.phone_number}"`
                 : ""
             }
          }
        ) {
          affected_rows
          returning{
            user_id
            email
            firstname
            lastname
            phone_number
            wallet_address
          }
        }
      }
    `;
    const resultUpdateProfile = await gql.Mutation(queryUpdateProfile);
    if (resultUpdateProfile.data.update_users.affected_rows) {
      runInAction(() => {
        this.state.myprofile =
          resultUpdateProfile.data.update_users.returning[0];
      });
    }
    return true;
  };

  public signUp = async (request: ISignUp) => {
    const formData = new FormData();
    formData.append("Email", request.email);
    formData.append("Firstname", request.firstname);
    formData.append("Lastname", request.lastname);
    formData.append("PhoneNumber", request.phoneNumber);
    formData.append("UserName", request.username);
    formData.append("WalletAddress", request.wallet_address);
    await this.restApi.create({
      request: formData,
      method: "sign-up",
      headers: { "Content-Type": "multipart/form-data" },
    });
    return true;
  };
  requestVerifyPhoneNumber() {
    return this.restApi.post({
      method: "request-otp",
      request: { phone_number: this.state.myprofile?.phone_number },
    });
  }
  verifyPhoneNumber(otp: string) {
    return this.restApi.post({
      method: "verify-otp",
      request: { phone_number: this.state.myprofile?.phone_number, code: otp },
    });
  }
  public signIn = async (request: {
    address: string;
    signature: string;
    nonce: string;
  }) => {
    this.state.myprofile = undefined;
    const result = await this.restApi.post({
      request: request,
      method: "sign-in",
    });
    if (result) {
      const access_token = result.data.access_token;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const decoded: any = jwt_decode(access_token);
      Cookies.set("x-access-token", access_token);
      // throw { message: "invalid user id" };
      localStorage.setItem(
        "user",
        JSON.stringify({ ...decoded, access_token: access_token }),
      );
      gql.updateWsLink();

      await this.fetchProfile({ userID: decoded.UserID });
      return await this.waitForProfile();
    } else {
      this.state.myprofile = null;
    }
    return true;
  };
  verifyEmail(qtoken: string) {
    return this.restApi.get({ method: `verify-email/${qtoken}`, id: "" });
  }
  public async resendEmail(email: string) {
    await this.restApi.post({
      method: "send-verification-email",
      request: { email },
    });
  }

  public signOut = async () => {
    if (this.unsubscriptionMyProfile) {
      this.unsubscriptionMyProfile();
    }
    localStorage.removeItem("user");
    Cookies.remove("x-access-token");
    gql.updateWsLink();

    runInAction(() => {
      this.state = {
        myprofile: null,
        myPayments: [],
        verificationDocuments: [],
      };
    });
    await disconnect();

    return true;
  };
  public uploadDocumentFile = async (request: {
    file: File;
    type: string;
  }): Promise<string> => {
    const formData = new FormData();
    formData.append("file", request.file);
    formData.append("type", request.type);
    const result = await this.restApi.post({
      request: formData,
      method: "upload-file",
      headers: { "Content-Type": "multipart/form-data" },
    });
    return result.data;
  };
  public createVerificationDocument = async (
    document: IVerificationDocument,
  ) => {
    const queryCreateVerificationDocument = `
      mutation createVerificationDocument($object: verification_documents_insert_input!) {
        insert_verification_documents_one(object: $object) {
          verification_document_id
        }
      }
     `;
    const result = await gql.Mutation(queryCreateVerificationDocument, {
      object: document,
    });
    if (result) {
      return result.data.insert_verification_documents_one
        .verification_document_id;
    }
  };
  public setDeleteVerificationDocument = async (document_id: string) => {
    runInAction(() => {
      this.state.verificationDocuments = this.state.verificationDocuments?.map(
        (document) => {
          if (document.documents_id === document_id) {
            document.status = "deleted";
          }
          return document;
        },
      );
    });
  };

  public updateFileVerificationDocument = async (request: {
    verification_document_id: string;
    file_key: string;
    file_name: string;
  }) => {
    const queryUpdateVerificationDocument = `
      mutation updateVerificationDocument($verification_document_id: uuid!, $file_key: String!, $file_name: String!) {
        update_verification_documents_by_pk(pk_columns: {verification_document_id: $verification_document_id}, _set: {file_key: $file_key, filename: $file_name}) {
          verification_document_id
        }
      }
    `;
    const result = await gql.Mutation(queryUpdateVerificationDocument, {
      verification_document_id: request.verification_document_id,
      file_key: request.file_key,
      file_name: request.file_name,
    });
    if (
      result &&
      result.data.update_verification_documents_by_pk.verification_document_id
    ) {
      return true;
    } else {
      throw new Error("Update verification document failed");
    }
  };
  public fetchMyPayments = async (user_id: string) => {
    const queryFetchPayments = `
      query fetchPayments {
        payments(where: {user_id: {_eq: "${user_id}"}}) {
          id
          payment_method_id
          user_id
          payment_method {
            payment_id
            payment_name
          }
          payment_attributes {
            id
            payment_id
            attribute_value
            payment_method_attribute_id
            payment_method_attribute {
              attribute_name
              attribute_label
              is_image
              required
              id
              preview_order
            }
          }
        }
      }
    `;
    const result = await gql.Query(queryFetchPayments);
    if (result && result.data.payments) {
      runInAction(() => {
        this.state.myPayments = result.data.payments;
      });
    }
  };
  public createPayment = async (request: {
    payment_method_id: string;
    label: string;
    attributes: IPaymentAttribute[];
  }) => {
    const queryCreatePayment = `
      mutation createPayment($object: payments_insert_input!) {
        insert_payments_one(object: $object) {
          id
        }
      }
    `;
    const result = await gql.Mutation(queryCreatePayment, {
      object: {
        payment_method_id: request.payment_method_id,
        label: request.label,
      },
    });
    if (result) {
      const payment_id = result.data.insert_payments_one.id;
      const queryCreatePaymentAttribute = `
        mutation createPaymentAttribute($objects: [payment_attributes_insert_input!]!) {
          insert_payment_attributes(objects: $objects) {
            affected_rows
          }
        }
      `;
      const resultCreatePaymentAttribute = await gql.Mutation(
        queryCreatePaymentAttribute,
        {
          objects: request.attributes.map((attribute) => ({
            payment_id,
            attribute_value: attribute.attribute_value,
            payment_method_attribute_id: attribute.payment_method_attribute_id,
          })),
        },
      );
      if (resultCreatePaymentAttribute) {
        if (
          resultCreatePaymentAttribute.data.insert_payment_attributes
            .affected_rows > 0
        ) {
          return payment_id;
        } else {
          throw new Error("Create payment attribute failed");
        }
      }
    }
  };
  public updatePayment = async (request: {
    payment_id: string;
    attributes: IPaymentAttribute[];
  }) => {
    const queryDeletePaymentAttribute = `
          mutation deletePaymentAttribute($payment_id: uuid!,$objects: [payment_attributes_insert_input!]!) {
            delete_payment_attributes(where: {payment_id: {_eq: $payment_id}}) {
              affected_rows
            }
            insert_payment_attributes(objects: $objects) {
              affected_rows
            }
          }
        `;
    const result = await gql.Mutation(queryDeletePaymentAttribute, {
      payment_id: request.payment_id,

      objects: request.attributes.map((attribute) => ({
        payment_id: request.payment_id,
        attribute_value: attribute.attribute_value,
        payment_method_attribute_id: attribute.payment_method_attribute_id,
      })),
    });
    if (
      result.data.delete_payment_attributes.affected_rows > 0 &&
      result.data.insert_payment_attributes.affected_rows > 0
    ) {
      return true;
    } else {
      throw new Error("Update payment attribute failed");
    }
  };
  //#endregion
}
